import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Autocomplete } from '@material-ui/lab';
import { Box, Checkbox, Grid, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import { colors } from '@/styles';

const DEFAULT_MENU_PROPS = {
    disablePortal: true,
    keepMounted: true,
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    getContentAnchorEl: null,
};

const ColumnFilterControls = ({
    controls = [],
    values = {},
    setValue,
    resolveOptions,
    menuProps,
    locationTypesMap,
}) => {
    const mergedMenuProps = useMemo(() => ({ ...DEFAULT_MENU_PROPS, ...(menuProps || {}) }), [menuProps]);

    const renderDateRange = (control) => {
        const [startKey, endKey] = control.keys;
        return (
            <Box key={control.label || startKey} mb={2}>
                <Typography variant="h6" gutterBottom>
                    {control.label}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            type="date"
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={values[startKey] || ''}
                            onChange={(e) => setValue(startKey, e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            type="date"
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={values[endKey] || ''}
                            onChange={(e) => setValue(endKey, e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const renderSelect = (control) => {
        const options = resolveOptions?.(control) || control.options || [];
        return (
            <Box key={control.key} mb={2}>
                <Typography variant="h6" gutterBottom>
                    {control.label}
                </Typography>
                <TextField
                    select
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={values[control.key] ?? ''}
                    onChange={(e) => setValue(control.key, e.target.value)}
                    SelectProps={{ MenuProps: mergedMenuProps }}
                >
                    {options.map((option) => (
                        <MenuItem key={`${control.key}-${option.value}`} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
        );
    };

    const renderMultiSelect = (control) => {
        const options = resolveOptions?.(control) || control.options || [];
        const selected = values[control.key] || [];
        return (
            <Box key={control.key} mb={2}>
                <Typography variant="h6" gutterBottom>
                    {control.label}
                </Typography>
                <Select
                    multiple
                    fullWidth
                    variant="outlined"
                    value={selected}
                    onChange={(e) =>
                        setValue(
                            control.key,
                            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                        )
                    }
                    renderValue={(vals) =>
                        vals.map((v) => options.find((opt) => opt.value === v)?.label || v).join(', ')
                    }
                    MenuProps={{
                        ...mergedMenuProps,
                        PaperProps: { style: { maxHeight: '50%' } },
                    }}
                >
                    {options.map((option) => (
                        <MenuItem key={`${control.key}-${option.value}`} value={option.value}>
                            <Checkbox checked={selected.indexOf(option.value) > -1} />
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        );
    };

    const renderMultiAutocomplete = (control) => {
        const options = resolveOptions?.(control) || [];
        const valueIds = values[control.key] || [];
        const valueOptions = options.filter((opt) => valueIds.includes(opt.value));

        return (
            <Box key={control.key} mb={2}>
                <Typography variant="h6" gutterBottom>
                    {control.label}
                </Typography>
                <Autocomplete
                    multiple
                    disablePortal
                    disableCloseOnSelect
                    options={options}
                    noOptionsText={control.noOptionsText}
                    getOptionSelected={(option, value) => option.value === value.value}
                    getOptionLabel={(option) => option.label}
                    value={valueOptions}
                    renderOption={(option, { selected }) =>
                        option.secondary ? (
                            // One line: the label, then muted secondary text cut off with an ellipsis.
                            // The full text is on hover.
                            <div
                                key={`${control.key}-${option.value}`}
                                title={`${option.label} · ${option.secondary}`}
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    min-width: 0;
                                    white-space: nowrap;
                                `}
                            >
                                <Checkbox color="primary" checked={selected} />
                                <span
                                    css={css`
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                    `}
                                >
                                    {option.label}
                                    <span
                                        css={css`
                                            color: ${colors.greys.primary};
                                            font-size: 0.875em;
                                        `}
                                    >
                                        {' · '}
                                        {option.secondary}
                                    </span>
                                </span>
                            </div>
                        ) : (
                            <li key={`${control.key}-${option.value}`}>
                                <Checkbox color="primary" checked={selected} />
                                {option.label}
                            </li>
                        )
                    }
                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                    onChange={(e, selectedValues = []) => {
                        const ids = selectedValues.map((opt) => opt.value);
                        setValue(control.key, ids);
                    }}
                />
            </Box>
        );
    };

    const renderCheckbox = (control) => (
        <Box key={control.key} mb={2}>
            <Grid container alignItems="center" spacing={1}>
                <Grid item>
                    <Checkbox
                        color="primary"
                        checked={Boolean(values[control.key])}
                        onChange={(e) => setValue(control.key, e.target.checked)}
                    />
                </Grid>
                <Grid item>
                    <Typography variant="body1">{control.label}</Typography>
                </Grid>
            </Grid>
        </Box>
    );

    const renderServiceType = (control) => {
        const options = resolveOptions?.(control) || [];
        const value = values.locationType || values.serviceType || '';

        return (
            <Box key={control.key} mb={2}>
                <Typography variant="h6" gutterBottom>
                    {control.label}
                </Typography>
                <TextField
                    select
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={value}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (locationTypesMap?.[val]) {
                            setValue('locationType', val);
                            setValue('serviceType', '');
                        } else {
                            setValue('serviceType', val);
                            setValue('locationType', '');
                        }
                    }}
                    SelectProps={{ MenuProps: mergedMenuProps }}
                >
                    {options.map((option) => (
                        <MenuItem key={`service-type-${option.value}`} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
        );
    };

    return (
        <>
            {(controls || []).map((control) => {
                if (control.type === 'dateRange') return renderDateRange(control);
                if (control.type === 'checkbox') return renderCheckbox(control);
                if (control.type === 'serviceType') return renderServiceType(control);
                if (control.type === 'select') return renderSelect(control);
                if (control.type === 'multiSelect') return renderMultiSelect(control);
                if (control.type === 'multiAutocomplete') return renderMultiAutocomplete(control);
                return null;
            })}
        </>
    );
};

export default ColumnFilterControls;
